import { DialogM } from "@common/components/alert/dialog/Dialog"
import { useOpenIntercomChat } from "@common/components/chat/IntercomChat"
import FilledButtonM from "@common/components/button/FilledButtonM"
import IconButtonM from "@common/components/button/IconButtonM"
import { ModalM } from "@common/components/modal/ModalM"
import { FormStepper } from "@common/components/stepper/FormStepper"
import TextM from "@common/components/text/TextM"
import TitleM from "@common/components/text/TitleM"
import { theme } from "@common/theme/theme"
import { useNavigation } from "@react-navigation/native"
import React, { useEffect, useMemo, useState } from "react"
import { ScrollView, View } from "react-native"
import { StatusBar } from "@common/systemBars/StatusBar"
import FontAwesome5 from "react-native-vector-icons/FontAwesome5"
import { connect } from "react-redux"

import { useApiStatus } from "@common/hooks/useApiStatus"
import { apiIdle, apiRequest } from "@data/redux/actions/api"
import { subscriptionService } from "@data/services/subscriptionService"

import base64 from "@common/utils/base64"
import {
    effectivePlanLabel,
    weeklyHoursCapacity,
} from "@common/utils/subscriptionCadence"
import { daysOptions } from "@data/constants/formConstants"

import ConstraintsGridStep from "@common/student-diagnostic-stepper/ConstraintsGridStep"
import HourAllocationStep from "@common/student-diagnostic-stepper/HourAllocationStep"
import StudentInfoStep from "@common/student-diagnostic-stepper/StudentInfoStep"
import { studentClasses } from "@data/constants/formConstants"
import {
    expandSlots,
    filterSchoolSubjectsByClass,
    squashSlots,
} from "@data/utility/Utils"
import { transformFormDataToObjectives as transformToObjectives } from "@data/utility/objectivesUtils"
import { SchoolSubjectBadge } from "../../tutor/students/ProfilDetails"
import { useNewSubscriptionStepper } from "../hooks/subscription/useNewSubscriptionStepper"
import { ScreenSkeleton } from "../screens/ScreenSkeleton"
import ObjectivesSummaryDisplay from "./ObjectivesSummaryDisplay"

// (ObjectivesStep removed; replaced by StudentObjectivesStep)

const StudentTutoringRequestUpdate = (props) => {
    const navigation = useNavigation()

    // Support chat opener — reused for the Cours d'été 409 safety net. Parent
    // mode is JWT-identified; the anonymous subscribe flow has no JWT, so only
    // require a fresh token when we're inside the parent portal.
    const openSupportChat = useOpenIntercomChat({
        requireFreshJwt: !!props.isParentMode,
    })

    const [summaryVisible, setSummaryVisible] = useState(false)
    const [submittedData, setSubmittedData] = useState(null)
    const [objectivesWithFormulatedGoals, setObjectivesWithFormulatedGoals] =
        useState([])

    const emailB64 = props.route?.params?.user ?? ""
    const email = base64.decode((emailB64 || "").replace(/-/g, ""))
    const subjectsOnly =
        props.route?.params?.subjectsOnly === true ||
        props.route?.params?.subjectsOnly === "true"
    const availabilityOnly =
        props.route?.params?.availabilityOnly === true ||
        props.route?.params?.availabilityOnly === "true"
    const fromParentPortal =
        props.route?.params?.fromParentPortal === true ||
        props.route?.params?.fromParentPortal === "true"

    const user = props.subscriptionUser

    const hoursPerWeek = weeklyHoursCapacity(user?.subscription_state, 2)
    const planLabel = effectivePlanLabel(user?.subscription_state)
    const minSelectedSlots = hoursPerWeek

    // Get user's current available slots (expand squashed format) or empty if none saved
    // Memoized to prevent infinite re-renders
    const userAvailableSlots = useMemo(() => {
        return user?.slots_possible
            ? expandSlots(user.slots_possible)
            : []
    }, [user?.slots_possible])

    const [formValues, setFormValues] = useState({
        school_subjects:
            user?.prioritized_school_subjects
                ?.map((s) => s.id)
                ?.slice(0, hoursPerWeek) ?? [],
        available_slots: [],
    })
    // objectives will be controlled via FormStepper control; no local state

    // Update available slots when user data changes
    useEffect(() => {
        setFormValues((prev) => ({
            ...prev,
            available_slots: userAvailableSlots,
        }))
    }, [userAvailableSlots])

    useEffect(() => {
        props.apiRequest(
            subscriptionService.getSubscribePricing,
            {},
            {},
            { email, continuity: 1 }
        )
    }, [])

    useApiStatus(
        subscriptionService.submitTutoringRequestUpdate,
        null,
        // We own error display below so we can intercept the Cours d'été 409
        // (`summer_course_active`) and route to support instead of a generic
        // dead-end error dialog.
        false,
        () => {
            setSummaryVisible(false)
            DialogM.show({
                text1: "Merci !",
                text2: availabilityOnly
                    ? "Les disponibilités ont bien été mises à jour."
                    : "Votre mise à jour a bien été enregistrée.",
            })
            // Navigate to parent-subscribe if in parent mode, otherwise to regular subscribe
            const targetScreen = props.isParentMode ? "menu" : "subscribe"
            navigation.replace(targetScreen, { user: base64.encode(email) })
        },
        (error) => {
            // Cours d'été actif : le backend refuse la mise à jour self-service
            // (les créneaux d'été contamineraient l'attribution de la rentrée).
            // On affiche son message + un accès direct au support plutôt qu'une
            // erreur générique sans issue.
            setSummaryVisible(false)
            if (error?.error_code === "summer_course_active") {
                DialogM.show({
                    text1: "Changement géré par le support",
                    text2:
                        error?.message ??
                        "Pendant le Cours d'été, ce changement se fait avec le support Masteur.",
                    buttonText1: "Contacter le support",
                    onPress: () => openSupportChat?.(),
                    buttonText3: "Fermer",
                    onPressBack: () => {},
                    exitButton: true,
                })
                props.apiIdle(subscriptionService.submitTutoringRequestUpdate)
                return
            }

            // Repli générique — miroir du dialog d'erreur par défaut de
            // useApiStatus (message backend + erreurs de validation éventuelles).
            const fieldErrors = error?.errors
                ? Object.keys(error.errors).map((key) => error.errors[key]?.[0])
                : []
            DialogM.show({
                text1: "Erreur",
                text2:
                    (error?.message ?? "Une erreur est survenue.") +
                    (fieldErrors.length ? "\n" + fieldErrors.join("\n") : ""),
                exitButton: true,
                onPressBack: () => {},
            })
            props.apiIdle(subscriptionService.submitTutoringRequestUpdate)
        }
    )

    useApiStatus(subscriptionService.getSubscribePricing, null, false, () => {})

    // Handle AI formulate responses
    useApiStatus(
        subscriptionService.formulateObjective,
        null,
        false,
        (data, allData, requestUrlParams, requestRouteParams) => {
            const objectiveIndex = requestRouteParams?.objectiveIndex
            const formulatedGoal = data?.formulated_goal

            if (typeof objectiveIndex === "number" && formulatedGoal) {
                // Update the objective with the formulated goal
                setObjectivesWithFormulatedGoals((prev) => {
                    const updated = [...prev]
                    if (updated[objectiveIndex]) {
                        updated[objectiveIndex] = {
                            ...updated[objectiveIndex],
                            formulated_goal: formulatedGoal,
                        }
                    }
                    return updated
                })
            }

            // Loading state is handled by Redux automatically
        },
        (error, meta) => {
            // Handle error - loading state is handled by Redux automatically
            const objectiveIndex = meta?.objectiveIndex
            console.log(
                `Failed to formulate objective ${objectiveIndex}:`,
                error
            )
        }
    )

    // Static form data with conditional visibility
    const supportType = formValues?.support_type

    const studentClass = formValues?.student_class
    const studentClassIndex = studentClasses.indexOf(studentClass)
    const isClassGreaterOrEqualTo2nde =
        studentClassIndex >= studentClasses.indexOf("2nde")

    const filteredSchoolSubjects = filterSchoolSubjectsByClass(
        props.schoolSubjects,
        studentClass
    )

    useEffect(() => {
        setFormValues((prev) => ({
            ...prev,
            support_type: null,
        }))
    }, [isClassGreaterOrEqualTo2nde])

    const { generateDefaultSubjectHoursAllocation } =
        useNewSubscriptionStepper()

    const formData = [
        {
            label: "Élève",
            name: "student_class",
            type: "custom",
            is_masked: availabilityOnly,
            defaultValue: user?.profile?.class ?? null,
            component: StudentInfoStep,
            componentProps: { user, hoursPerWeek },
            showNextButton: true,
        },
        {
            label: "Accompagnement souhaité",
            name: "support_type",
            type: "select.single",
            is_masked: availabilityOnly,
            options: [
                {
                    label: "Soutien sur une ou plusieurs matières",
                    value: "subject_support",
                    description:
                        "Renforcement ciblé pour améliorer les résultats",
                },
                {
                    label: "Aide aux devoirs",
                    value: "homework_help",
                    description:
                        "Accompagnement sur plusieurs matières pour les devoirs quotidiens",
                    // disabled if class greater or equal to 2nde (use index in studentClasses)
                    disabled: isClassGreaterOrEqualTo2nde,
                },
            ],
            defaultValue: null,
            validationRules: {
                required: true,
            },
            height: 90,
            description: "Quel type d'aide souhaitez-vous ?",
            showNextButton: formValues?.selected_subjects?.length > 0,
        },
        // Subject selection step for subject_support flow only
        {
            label: "Matières",
            name: "selected_subjects",
            type: "select.multiple",
            is_masked: availabilityOnly || supportType !== "subject_support", // Hide if not subject support or availabilityOnly
            maxSelection: hoursPerWeek,
            options: (filteredSchoolSubjects || []).map((s) => ({
                label: s.name,
                value: s.id,
                icon: s.icon,
            })),
            defaultValue: [],
            validationRules: {
                minLength: supportType === "subject_support" ? 1 : 0,
                customCondition: (value) => {
                    if (supportType !== "subject_support") return true
                    return Array.isArray(value) && value.length >= 1
                },
            },
            description: `Choisissez les matières pour lesquelles vous souhaitez un soutien`,
            topComponent: (() => {
                const selectedCount = formValues?.selected_subjects?.length || 0
                const isLimitReached = selectedCount >= hoursPerWeek

                if (isLimitReached) {
                    return (
                        <TextM
                            color={theme.colors.accentDark}
                            fontSize={12}
                            wrap
                            style={{ marginTop: 10, textAlign: "center" }}
                        >
                            ✓ Limite atteinte ({selectedCount}/{hoursPerWeek}{" "}
                            matière{selectedCount > 1 ? "s" : ""}) • Chaque
                            matière nécessite au moins 1h/semaine
                        </TextM>
                    )
                } else {
                    return (
                        <TextM
                            color={theme.colors.grey}
                            fontSize={12}
                            wrap
                            style={{ marginTop: 10, textAlign: "center" }}
                        >
                            {selectedCount} matière
                            {selectedCount > 1 ? "s" : ""} choisie
                            {selectedCount > 1 ? "s" : ""} • Chaque matière
                            nécessite au moins 1h/semaine
                        </TextM>
                    )
                }
            })(),
            showNextButton: formValues?.selected_subjects?.length > 0,
        },
        // Hour allocation step for subject_support flow only (skip if only one possibility)
        {
            label: "Répartition des heures",
            name: "subject_hours_allocation",
            type: "custom",
            is_masked: (() => {
                if (availabilityOnly || supportType !== "subject_support")
                    return true // Hide if availabilityOnly or not subject support

                const selectedSubjects = formValues?.selected_subjects || []

                // Skip step if there's only one mathematical possibility:
                // 1. Single subject selected (all hours go to that subject)
                // 2. Number of subjects equals hours per week (1h each)
                const shouldSkipHourAllocation =
                    selectedSubjects.length === 1 || // Single subject: all hours to it
                    selectedSubjects.length === hoursPerWeek // Equal distribution: 1h each

                return shouldSkipHourAllocation // Hide step when no choice needed
            })(),
            component: HourAllocationStep,
            componentProps: {
                hoursPerWeek,
                selectedSubjects: formValues?.selected_subjects || [],
                schoolSubjects: filteredSchoolSubjects,
                generateDefaultSubjectHoursAllocation,
            },
            defaultValue: (() => {
                if (supportType !== "subject_support") return {}
                const selectedSubjects = formValues?.selected_subjects || []
                if (selectedSubjects.length === 0) return {}

                // Generate equal distribution as default
                const baseHours = Math.floor(
                    hoursPerWeek / selectedSubjects.length
                )
                const extraHours = hoursPerWeek % selectedSubjects.length

                const allocation = {}
                selectedSubjects.forEach((subjectId, index) => {
                    allocation[subjectId] =
                        baseHours + (index < extraHours ? 1 : 0)
                })
                return allocation
            })(),
            validationRules: {
                customCondition: (value) => {
                    if (supportType !== "subject_support") return true
                    const selectedSubjects = formValues?.selected_subjects || []
                    if (selectedSubjects.length === 0) return true

                    const totalHours = selectedSubjects.reduce(
                        (sum, subjectId) => {
                            return sum + (value?.[subjectId] || 0)
                        },
                        0
                    )
                    return totalHours === hoursPerWeek
                },
            },
            description: `Répartissez vos ${hoursPerWeek} heures hebdomadaires entre vos matières`,
            bottomComponent: (
                <TextM
                    color={theme.colors.grey}
                    fontSize={12}
                    wrap
                    style={{ marginTop: 10, textAlign: "center" }}
                >
                    Chaque matière doit avoir au moins 1h
                </TextM>
            ),
            showNextButton: true,
        },
        // Generate dynamic needs selection steps for each selected subject (subject_support flow)
        ...(() => {
            if (availabilityOnly || supportType !== "subject_support") return []

            const selectedSubjects = formValues?.selected_subjects || []

            return selectedSubjects.map((subjectId) => {
                const subject = (filteredSchoolSubjects || []).find(
                    (s) => s.id === subjectId
                )

                return {
                    label: `Objectifs de ${user?.firstname}`,
                    name: `needs_subject_${subjectId}`,
                    type: "select.multiple",
                    maxSelection: 2,
                    options: (props.objectiveNeeds || []).map((need) => ({
                        label: need.label,
                        value: need.key,
                        leftComponent: need.icon ? (
                            <FontAwesome5
                                name={need.icon}
                                size={20}
                                color={theme.colors.primary}
                                style={{ marginRight: 12 }}
                            />
                        ) : null,
                    })),
                    defaultValue: [],
                    validationRules: {
                        customCondition: (value) =>
                            !Array.isArray(value) || value.length <= 2,
                    },
                    topComponent: (
                        <View
                            style={{
                                marginBottom: 10,
                                flexDirection: "row",
                                alignItems: "center",
                                justifyContent: "flex-start",
                                flexWrap: "wrap",
                            }}
                        >
                            <TextM fontWeight="Bold" fontSize={14}>
                                En
                            </TextM>
                            <SchoolSubjectBadge
                                iconWidth={16}
                                fontSize={14}
                                style={{ maxWidth: null, marginLeft: 4 }}
                                schoolSubject={subject}
                            />
                            <TextM fontWeight="Bold" fontSize={14}>
                                , {user?.firstname} a besoin de...
                            </TextM>
                        </View>
                    ),
                    bottomComponent: (
                        <TextM
                            color={theme.colors.grey}
                            fontSize={12}
                            wrap
                            style={{ marginTop: 10, textAlign: "center" }}
                        >
                            Sélectionnez jusqu&apos;à 2 besoins pour cette
                            matière
                        </TextM>
                    ),
                }
            })
        })(),
        // Priority subjects step for homework_help flow only
        {
            label: "Matières les plus prioritaires pour les devoirs",
            name: "priority_subjects",
            type: "select.multiple",
            ordered: true,
            is_masked: availabilityOnly || supportType !== "homework_help", // Hide if availabilityOnly or not homework help
            maxSelection: hoursPerWeek * 2,
            options: (filteredSchoolSubjects || []).map((s) => ({
                label: s.name,
                value: s.id,
                icon: s.icon,
            })),
            defaultValue:
                user?.prioritized_school_subjects
                    ?.map((s) => s.id)
                    ?.slice(0, hoursPerWeek) || [],
            validationRules: {
                minLength: supportType === "homework_help" ? 1 : 0,
                customCondition: (value) => {
                    if (supportType !== "homework_help") return true
                    return Array.isArray(value) && value.length >= 1
                },
            },
            description: `Dans quelles matières votre enfant a-t-il le plus besoin d'aide pour ses devoirs ? Choisissez par ordre de priorité.`,
            topComponent: (() => {
                const selectedCount = formValues?.priority_subjects?.length || 0
                const isLimitReached = selectedCount >= hoursPerWeek * 2

                if (isLimitReached) {
                    return (
                        <TextM
                            color={theme.colors.success}
                            fontSize={12}
                            wrap
                            style={{ marginTop: 10, textAlign: "center" }}
                        >
                            ✓ Limite atteinte ({selectedCount}/
                            {hoursPerWeek * 2} matières) • Le tuteur pourra
                            aider sur d&apos;autres matières selon les devoirs
                            du jour
                        </TextM>
                    )
                } else {
                    return (
                        <TextM
                            color={theme.colors.grey}
                            fontSize={12}
                            wrap
                            style={{ marginTop: 10, textAlign: "center" }}
                        >
                            {selectedCount} matières prioritaires choisies • Le
                            tuteur pourra aider sur d&apos;autres matières selon
                            les devoirs du jour
                        </TextM>
                    )
                }
            })(),
            showNextButton: true,
        },
        // Time slots step - hidden when subjectsOnly mode
        {
            label: `Disponibilités de ${user?.firstname} pour les cours Masteur`,
            name: "available_slots",
            type: "custom",
            is_masked: subjectsOnly,
            description: `Sélectionnez au minimum ${minSelectedSlots} créneaux pour votre planning personnalisé`,
            defaultValue: userAvailableSlots,
            component: ConstraintsGridStep,
            componentProps: {
                user,
                hoursPerWeek,
                includeSunday:
                    planLabel?.toLowerCase().includes("integral") ||
                    planLabel?.toLowerCase().includes("particuliers"),
            },
            showNextButton: true,
        },
    ]

    // Transform form data back to expected objectives format using shared utility
    const transformFormDataToObjectives = (formData) => {
        const objectives = transformToObjectives(
            formData,
            hoursPerWeek,
            filteredSchoolSubjects || []
        )
        return { ...formData, objectives }
    }

    // Function to call AI formulate for each objective using Redux patterns
    const formulateObjectivesWithAI = (objectives) => {
        if (!objectives || objectives.length === 0) return

        setObjectivesWithFormulatedGoals(objectives) // Initialize with current objectives

        // Make API calls for each objective using Redux pattern
        objectives.forEach((objective, i) => {
            const payload = {
                email,
                type: objective.type,
                subject_ids: objective.subject_ids,
                needs: objective.needs || [],
                comment: objective.comment || null,
            }

            // Use the standard Redux API pattern
            props.apiRequest(
                subscriptionService.formulateObjective,
                { objectiveIndex: i },
                payload
            )
        })
    }

    const onSubmit = (data) => {
        // Validate minimum slots selected (skip in subjectsOnly mode)
        if (
            !subjectsOnly &&
            (data.available_slots?.length ?? 0) < minSelectedSlots
        ) {
            DialogM.show({
                text1: "Créneaux insuffisants",
                text2: `Veuillez sélectionner au minimum ${minSelectedSlots} créneaux pour votre planning personnalisé`,
            })
            return
        }

        // Squash slots before saving (skip in subjectsOnly mode)
        const squashedSlots = subjectsOnly
            ? undefined
            : squashSlots(data.available_slots)

        // In availabilityOnly mode, submit directly without summary
        if (availabilityOnly) {
            const doSubmit = () => {
                props.apiRequest(
                    subscriptionService.submitTutoringRequestUpdate,
                    {},
                    {
                        available_slots: squashedSlots,
                        email,
                    }
                )
            }

            if (fromParentPortal) {
                DialogM.show({
                    text1: "Attention",
                    text2: "Ces changements sont définitifs et peuvent entraîner un changement de tuteur. Souhaitez-vous confirmer ?",
                    buttonText1: "Confirmer",
                    buttonText2: "Annuler",
                    onPress: doSubmit,
                })
            } else {
                doSubmit()
            }
            return
        }

        const dataForTransform = { ...data }
        if (subjectsOnly) {
            delete dataForTransform.available_slots
        } else {
            dataForTransform.available_slots = squashedSlots
        }
        const transformedData = transformFormDataToObjectives(dataForTransform)
        setSubmittedData({ ...transformedData, email })
        setSummaryVisible(true)

        // Trigger AI formulation when modal opens
        if (
            transformedData.objectives &&
            transformedData.objectives.length > 0
        ) {
            formulateObjectivesWithAI(transformedData.objectives)
        }
    }

    const frDay = (enDay) =>
        daysOptions.find((d) => d.value === enDay)?.label || enDay

    const renderSummary = () => {
        if (!submittedData) return null
        const fullname = (() => {
            const fn = (user?.firstname || "").trim()
            const ln = (user?.lastname || "").trim()
            if (fn && ln) return `${fn} ${ln.charAt(0)}.`
            return user?.display_name || `${fn} ${ln}`.trim()
        })()
        // Get available (green) slots
        const available = submittedData.available_slots || []

        // Group by day for compact display
        const grouped = available.reduce((acc, slot) => {
            const [day, time] = slot.split(" ")
            const key = frDay(day)
            acc[key] = acc[key] ? acc[key].concat(time) : [time]
            return acc
        }, {})

        return (
            <ScrollView
                style={{ maxHeight: "100%" }}
                contentContainerStyle={{
                    paddingHorizontal: 16,
                    paddingBottom: 16,
                }}
            >
                <View
                    style={{
                        flexDirection: "row",
                        alignItems: "center",
                        justifyContent: "space-between",
                        marginTop: 12,
                        marginBottom: 12,
                    }}
                >
                    <TitleM fontWeight="ExtraBold">Récapitulatif</TitleM>
                    <IconButtonM
                        icon="times"
                        size={20}
                        onPress={() => setSummaryVisible(false)}
                        iconColor={theme.colors.primary}
                    />
                </View>
                {/* Infos card */}
                <View
                    style={{
                        backgroundColor: theme.colors.white,
                        borderRadius: 12,
                        borderWidth: 1,
                        borderColor: theme.colors.lightMore,
                        padding: 12,
                        marginBottom: 12,
                    }}
                >
                    <TextM fontWeight="Bold" style={{ marginBottom: 2 }}>
                        Élève
                    </TextM>
                    <TextM style={{ marginBottom: 8 }}>{fullname}</TextM>

                    <TextM fontWeight="Bold" style={{ marginBottom: 2 }}>
                        Classe
                    </TextM>
                    <TextM style={{ marginBottom: 8 }}>
                        {submittedData?.student_class ||
                            user?.profile?.class ||
                            ""}
                    </TextM>

                    <TextM fontWeight="Bold" style={{ marginBottom: 2 }}>
                        Nombre d&apos;heures par semaine
                    </TextM>
                    <TextM style={{ marginBottom: 8 }}>
                        {hoursPerWeek} heure{hoursPerWeek > 1 ? "s" : ""}
                    </TextM>
                </View>

                {/* Objectives card */}
                <View
                    style={{
                        backgroundColor: theme.colors.white,
                        borderRadius: 12,
                        borderWidth: 1,
                        borderColor: theme.colors.lightMore,
                        padding: 12,
                        marginBottom: 12,
                    }}
                >
                    <TextM fontWeight="Bold" style={{ marginBottom: 8 }}>
                        Objectifs pédagogiques
                    </TextM>
                    <ObjectivesSummaryDisplay
                        objectives={
                            objectivesWithFormulatedGoals.length > 0
                                ? objectivesWithFormulatedGoals
                                : submittedData?.objectives || []
                        }
                        schoolSubjects={filteredSchoolSubjects || []}
                        objectiveNeeds={props.objectiveNeeds || []}
                        compact={true}
                        style={{ marginTop: 0 }}
                        isFormulateLoading={props.formulateObjectiveRequest}
                        hideStatus
                    />
                </View>

                {/* Constraints card: compact chips like step 3 (hidden in subjectsOnly mode) */}
                {!subjectsOnly && (
                    <View
                        style={{
                            backgroundColor: theme.colors.white,
                            borderRadius: 12,
                            borderWidth: 1,
                            borderColor: theme.colors.lightMore,
                            padding: 12,
                        }}
                    >
                        <TextM
                            fontWeight="Bold"
                            style={{ marginBottom: 8 }}
                            wrap
                        >
                            Créneaux possibles pour les cours de{" "}
                            {user?.firstname}
                        </TextM>
                        {available.length === 0 ? (
                            <TextM color={theme.colors.light}>
                                Aucune disponibilité
                            </TextM>
                        ) : (
                            Object.keys(grouped).map((day) => (
                                <View
                                    key={day}
                                    style={{
                                        marginBottom: 6,
                                        flexDirection: "row",
                                        alignItems: "flex-start",
                                    }}
                                >
                                    <TextM
                                        fontWeight="Medium"
                                        fontSize={12}
                                        style={{
                                            marginRight: 8,
                                            paddingTop: 2,
                                        }}
                                    >
                                        {day}
                                    </TextM>
                                    <View
                                        style={{
                                            flex: 1,
                                            flexDirection: "row",
                                            flexWrap: "wrap",
                                        }}
                                    >
                                        {grouped[day].map((time) => (
                                            <View
                                                key={`${day}-${time}`}
                                                style={{
                                                    backgroundColor:
                                                        theme.colors
                                                            .successLight,
                                                    borderRadius: 7,
                                                    paddingVertical: 3,
                                                    paddingHorizontal: 6,
                                                    marginRight: 4,
                                                    marginBottom: 4,
                                                    flexDirection: "row",
                                                    alignItems: "center",
                                                    borderWidth: 1,
                                                    borderColor:
                                                        theme.colors.success,
                                                }}
                                            >
                                                <FontAwesome5
                                                    name="check"
                                                    size={12}
                                                    color={theme.colors.success}
                                                    style={{ marginRight: 4 }}
                                                />
                                                <TextM
                                                    fontSize={10}
                                                    fontWeight="Bold"
                                                    color={theme.colors.success}
                                                >
                                                    {time}
                                                </TextM>
                                            </View>
                                        ))}
                                    </View>
                                </View>
                            ))
                        )}
                    </View>
                )}

                <FilledButtonM
                    style={{ marginTop: 14, width: "90%", marginBottom: 5 }}
                    label="Confirmer"
                    onPress={() => {
                        setSummaryVisible(false)
                        // Use objectives with formulated goals if available
                        const finalData = {
                            ...submittedData,
                            objectives:
                                objectivesWithFormulatedGoals.length > 0
                                    ? objectivesWithFormulatedGoals
                                    : submittedData.objectives,
                        }
                        props.apiRequest(
                            subscriptionService.submitTutoringRequestUpdate,
                            {},
                            finalData
                        )
                    }}
                    color={theme.colors.success}
                />
                <TextM
                    color={theme.colors.light}
                    fontSize={11}
                    wrap
                    style={{ marginTop: 2, textAlign: "center" }}
                >
                    Vous pourrez modifier ces choix plus tard
                </TextM>
            </ScrollView>
        )
    }

    return (
        <ScreenSkeleton
            errorScreenProps={props.error ? { error: props.error } : null}
            isLoading={props.request}
        >
            <View style={{ flex: 1, alignItems: "center" }}>
                <FormStepper
                    navigation={navigation}
                    track
                    formData={formData?.filter(
                        (step) => step.is_masked !== true
                    )}
                    champion={null}
                    formValues={formValues}
                    setFormValues={setFormValues}
                    onSubmit={onSubmit}
                    submitted={props.submitTutoringRequestUpdate}
                    submittedMessage=""
                    serverError={null}
                    submitButtonText={
                        availabilityOnly
                            ? "Je confirme mes disponibilités"
                            : subjectsOnly
                              ? "Je confirme mes matières"
                              : "Je confirme mes choix"
                    }
                    syncFormValues={true}
                />

                <ModalM
                    visible={summaryVisible}
                    style={{ maxHeight: "90%" }}
                    onDismiss={() => setSummaryVisible(false)}
                >
                    {renderSummary()}
                </ModalM>

                <StatusBar
                    style="dark"
                    backgroundColor={theme.colors.white}
                    translucent={false}
                />
            </View>
        </ScreenSkeleton>
    )
}

const mapStateToProps = (state) => {
    return {
        subscriptionUser: state.data.formData?.user,
        request: state.api.getSubscribePricing?.request,
        error: state.api.getSubscribePricing?.error,
        schoolSubjects: state.data.formData?.schoolSubjects ?? [],
        objectiveNeeds: state.data.formData?.objectiveNeeds ?? [],
        submitTutoringRequestUpdate:
            state.api.submitTutoringRequestUpdate?.request,
        formulateObjectiveRequest: state.api.formulateObjective?.request,
        isParentMode: state.data.family?.isParentMode,
    }
}

const mapDispatchToProps = {
    apiRequest,
    apiIdle,
}

/**
 * Porte de la route `subscription-continuity` (et de son alias mort
 * `student-tutoring-request-update`). L'écran n'est PLUS monté : il écrivait
 * hors du circuit demande (slots_possible, objectifs sans demande, ticket
 * new_need) alors que l'intake ops canonique est la DEMANDE DE TUTORAT. Un
 * lien resté dans la nature (campagne HubSpot `subscribe_continuity_url`,
 * vieux mail) atterrit sur la page Abonnement, qui porte la narration — même
 * idiome que la porte du cockpit d'été (SummerCockpitRoute).
 *
 * `parent-subscription` est un onglet du navigateur parent, pas une route de
 * cette pile : on passe par `menu`, l'écran racine qui porte le navigateur
 * d'onglets. En mode élève, `menu` porte les onglets élève : la redirection y
 * atterrit sur la home — pas d'écran blanc, pas d'erreur de navigation.
 *
 * L'écran et son fix de rendu restent dans le bundle en filet, le temps que
 * le self « modifier mon besoin » rebranché sur le circuit demande le
 * remplace (voir l'issue de suivi référencée par la PR de retrait).
 */
const SubscriptionContinuityRoute = () => {
    const routeNavigation = useNavigation()

    useEffect(() => {
        routeNavigation.navigate("menu", { screen: "parent-subscription" })
    }, [routeNavigation])

    return null
}

export default SubscriptionContinuityRoute

export const LegacyStudentTutoringRequestUpdate = connect(
    mapStateToProps,
    mapDispatchToProps
)(StudentTutoringRequestUpdate)
